import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Alert,
  Typography,
  Spin,
  Row,
  Col,
} from "antd";
import { loadStripe } from "@stripe/stripe-js";
import { useSelector, useDispatch } from "react-redux";
import { setUser } from "../../../app.slice";
import {
  useGetProPricingQuery,
  useConfirmSuppliesPaymentMutation,
  useListMySuppliesOrdersQuery,
} from "../payment.services";
import { toast } from "react-toastify";
import { getPrefillShippingAddress } from "./suppliesAddressPrefill";
import { findLatestSuppliesOrder } from "./suppliesOrderTracking";
import SuppliesOrderTrackingModal from "./SuppliesOrderTrackingModal";

const { Text, Title } = Typography;
const BRAND = "#6DB33F";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    "pk_test_51RY3xZDw6TmwGTtebB5OiP6KcmZJLzDLIssD55qY3kXeEZyanHMRU03r29Yo8Q4jEs4DOVCyQEoiLy3QRKPFh9ej00zGlxXvOK"
);

const CheckoutForm = ({ price, cardOnFile, onDone }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.app.user);
  const [form] = Form.useForm();
  const [paying, setPaying] = useState(false);
  const [trackOpen, setTrackOpen] = useState(false);
  const [prefilledFromOrder, setPrefilledFromOrder] = useState(false);
  const [prefilledFromStored, setPrefilledFromStored] = useState(false);
  const [confirmPay] = useConfirmSuppliesPaymentMutation();
  const { data: suppliesData, refetch: refetchOrders } =
    useListMySuppliesOrdersQuery();
  const currentOrder = findLatestSuppliesOrder(
    suppliesData?.orders,
    "printer"
  );

  const cardLast4 = cardOnFile?.last4 || user?.lastFourDigits || "****";
  const cardBrand = (cardOnFile?.brand || "card")
    .toString()
    .replace(/^./, (c) => c.toUpperCase());

  useEffect(() => {
    const prefill = getPrefillShippingAddress(user, suppliesData?.orders);
    form.setFieldsValue({
      shippingStreet: prefill.shippingStreet,
      shippingCity: prefill.shippingCity,
      shippingState: prefill.shippingState,
      shippingPostalCode: prefill.shippingPostalCode,
    });
    setPrefilledFromOrder(Boolean(prefill.fromPreviousOrder));
    setPrefilledFromStored(Boolean(prefill.fromStoredAddress));
  }, [user, suppliesData, form]);

  const waitForOrder = async (paymentIntentId) => {
    const token = localStorage.getItem("token");
    for (let i = 0; i < 30; i++) {
      const res = await fetch(
        `/api/payment/check-printer-status/${paymentIntentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.printerOrdered) {
        const updatedUser = {
          ...user,
          hasPrinter: true,
          printerOrdered: true,
        };
        dispatch(setUser(updatedUser));
        localStorage.setItem("user", JSON.stringify(updatedUser));
        return { ok: true };
      }
      if (
        data.refunded ||
        data.status === "failed" ||
        data.status === "refunded"
      ) {
        return {
          ok: false,
          reason:
            data.failureReason ||
            "Fulfilment failed and payment was refunded.",
        };
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    return { ok: false, reason: "Order is still processing. Check back shortly." };
  };

  const onPay = async (values) => {
    setPaying(true);
    try {
      let result = await confirmPay({
        productType: "printer",
        shippingStreet: values.shippingStreet,
        shippingCity: values.shippingCity,
        shippingState: values.shippingState || "",
        shippingPostalCode: values.shippingPostalCode,
        shippingCountry: "GB",
        userId: user._id,
        amount: price,
        currentPassword: values.currentPassword,
      }).unwrap();

      if (result.requiresAction && result.clientSecret) {
        const stripe = await stripePromise;
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          result.clientSecret
        );
        if (error) throw new Error(error.message);
        if (paymentIntent?.status !== "succeeded") {
          throw new Error("Payment was not completed");
        }
        result = {
          ...result,
          paymentIntentId: paymentIntent.id,
          requiresAction: false,
        };
      }

      toast.info("Payment received — confirming order…");
      const status = await waitForOrder(result.paymentIntentId);
      if (!status.ok) {
        toast.error(status.reason);
        return;
      }

      toast.success("Printer ordered — Amazon will ship it.");
      refetchOrders?.();
      onDone?.();
    } catch (err) {
      toast.error(err?.data?.message || err.message || "Payment failed");
    } finally {
      setPaying(false);
    }
  };

  return (
    <>
    <Form form={form} layout="vertical" onFinish={onPay} requiredMark={false}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 12,
          paddingBottom: 12,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <div>
          <Title level={5} style={{ margin: 0 }}>
            Date label printer
          </Title>
          <Text type="secondary">Delivered in about 3–5 working days</Text>
        </div>
        <Text strong style={{ fontSize: 20, color: BRAND }}>
          £{price.toFixed(2)}
        </Text>
      </div>

      {currentOrder && (
        <Button
          type="default"
          block
          size="large"
          onClick={() => setTrackOpen(true)}
          style={{ marginBottom: 16 }}
        >
          View current order / tracking
        </Button>
      )}

      <Text strong style={{ display: "block", marginBottom: 8 }}>
        Delivery address (UK)
      </Text>
      {prefilledFromOrder && (
        <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
          Prefilled from your last supplies order — edit if needed.
        </Text>
      )}
      {!prefilledFromOrder && prefilledFromStored && (
        <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
          Prefilled from your saved address — edit if needed.
        </Text>
      )}
      <Form.Item
        name="shippingStreet"
        label="Street"
        rules={[{ required: true, message: "Required" }]}
      >
        <Input placeholder="Street address" size="large" />
      </Form.Item>
      <Row gutter={12}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="shippingCity"
            label="City"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="City" size="large" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="shippingPostalCode"
            label="Postcode"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="e.g. M1 1AE" size="large" />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="shippingState" label="County (optional)">
        <Input placeholder="County" size="large" />
      </Form.Item>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={`Pay with ${cardBrand} ending ****${cardLast4}`}
        description="Charged to the card on your SFBB subscription. Confirm with your account password below."
      />

      <Form.Item
        name="currentPassword"
        label="Account password (required to confirm)"
        rules={[
          { required: true, message: "Enter your password to confirm payment" },
        ]}
      >
        <Input.Password
          placeholder="Your SFBB login password"
          size="large"
          autoComplete="current-password"
        />
      </Form.Item>

      <Button
        type="primary"
        htmlType="submit"
        size="large"
        block
        loading={paying}
        disabled={paying}
        style={{
          background: BRAND,
          borderColor: BRAND,
          height: 48,
          fontWeight: 600,
        }}
      >
        {paying ? "Processing…" : `Pay £${price.toFixed(2)}`}
      </Button>
    </Form>
    <SuppliesOrderTrackingModal
      open={trackOpen}
      onClose={() => setTrackOpen(false)}
      order={currentOrder}
      productLabel="printer order"
    />
    </>
  );
};

const AddPrinterPanel = ({ onSuccess }) => {
  const user = useSelector((state) => state.app.user);
  const { data: pricingData, isLoading, error } = useGetProPricingQuery();
  const price = pricingData?.data?.printerPrice || 99.95;
  const cardOnFile = pricingData?.data?.cardOnFile;
  const hasPaymentMethod =
    pricingData?.data?.hasPaymentMethod ||
    !!(cardOnFile?.last4 || user?.lastFourDigits);

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: 32 }}>
        <Spin />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Could not load price"
        description="Please refresh and try again."
      />
    );
  }

  if (!hasPaymentMethod) {
    return (
      <Alert
        type="warning"
        showIcon
        message="No card on file"
        description="Add or update your payment method in My Account first. Supplies are charged to your subscription card."
      />
    );
  }

  return (
    <CheckoutForm
      price={price}
      cardOnFile={cardOnFile}
      onDone={onSuccess}
    />
  );
};

export default AddPrinterPanel;
